import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const API = 'https://csksportswear.in/backend/api';

function GalleryManagement() {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadType, setUploadType] = useState('image');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('csk_token');
  const user = JSON.parse(localStorage.getItem('csk_user') || '{}');

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const res = await axios.get(`${API}/gallery.php?action=list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) setGallery(res.data.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) { alert('Please select a file!'); return; }
    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', title);
      formData.append('description', description);
      const res = await axios.post(`${API}/upload.php?action=gallery`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / e.total)),
      });
      if (res.data.success) {
        setShowUploadModal(false);
        setSelectedFile(null); setPreview(null);
        setTitle(''); setDescription('');
        setUploadProgress(0);
        fetchGallery();
        alert('Upload successful!');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Upload failed!');
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.get(`${API}/gallery.php?action=delete&id=${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchGallery();
    } catch (err) { alert('Error!'); }
  };

  const menuItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: '🏠' },
    { path: '/admin/inventory', label: 'Inventory', icon: '📦' },
    { path: '/admin/pos', label: 'POS Billing', icon: '🧾' },
    { path: '/admin/reports', label: 'Reports', icon: '📊' },
    { path: '/admin/gallery', label: 'Gallery', icon: '🖼️' },
    { path: '/admin/enquiries', label: 'Enquiries', icon: '📩' },
    ...(user.role === 'admin' ? [{ path: '/admin/users', label: 'Users', icon: '👥' }] : []),
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#0A0A0A', fontFamily: 'Inter, sans-serif' }}>
      <style>{`
        .gal-sidebar { width: 200px; background: #000; border-right: 1px solid rgba(255,255,255,0.08); display: flex; flex-direction: column; position: fixed; top: 0; left: 0; bottom: 0; z-index: 100; transition: transform 0.3s ease; }
        .gal-main { margin-left: 200px; flex: 1; padding: 32px; }
        .gal-mobile-hdr { display: none; background: #000; padding: 12px 16px; position: fixed; top: 0; left: 0; right: 0; z-index: 99; border-bottom: 1px solid rgba(255,255,255,0.08); align-items: center; justify-content: space-between; }
        .gal-overlay { display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.7); z-index: 99; }
        @media (max-width: 768px) { .gal-sidebar { transform: translateX(-100%); } .gal-sidebar.open { transform: translateX(0); } .gal-main { margin-left: 0; padding: 72px 16px 16px; } .gal-mobile-hdr { display: flex; } .gal-overlay.show { display: block; } .gal-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 480px) { .gal-grid { grid-template-columns: 1fr 1fr !important; gap: 8px !important; } }
        .gal-nav-link { display: flex; align-items: center; gap: 10px; padding: 12px 16px; color: #475569; font-size: 13px; font-weight: 500; text-decoration: none; border-left: 3px solid transparent; transition: all 0.2s; }
        .gal-nav-link:hover { color: white; background: rgba(255,255,255,0.05); }
        .gal-nav-link.active { color: white; background: rgba(0,102,255,0.1); border-left-color: #0066FF; }
        .gal-item { position: relative; border-radius: 12px; overflow: hidden; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); aspect-ratio: 1; cursor: pointer; }
        .gal-item:hover .gal-overlay-hover { opacity: 1 !important; }
        .gal-overlay-hover { position: absolute; inset: 0; background: rgba(0,0,0,0.6); opacity: 0; transition: opacity 0.2s; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; }
        .upload-zone { border: 2px dashed rgba(0,102,255,0.3); border-radius: 12px; padding: 32px; text-align: center; cursor: pointer; transition: all 0.2s; background: rgba(0,102,255,0.02); }
        .upload-zone:hover { border-color: #0066FF; background: rgba(0,102,255,0.05); }
      `}</style>

      {/* Mobile Header */}
      <div className="gal-mobile-hdr">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ color: '#DC2626', fontSize: '20px' }}>★</span>
          <span style={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 800, color: 'white' }}>CSK</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '22px', cursor: 'pointer' }}>☰</button>
      </div>

      <div className={`gal-overlay ${sidebarOpen ? 'show' : ''}`} onClick={() => setSidebarOpen(false)} />

      {/* Sidebar */}
      <div className={`gal-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#DC2626', fontSize: '20px' }}>★</span>
            <div>
              <div style={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 800, letterSpacing: '2px', color: 'white' }}>CSK</div>
              <div style={{ fontSize: '8px', color: '#475569', letterSpacing: '1px' }}>ADMIN PANEL</div>
            </div>
          </div>
        </div>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'white' }}>{user.name}</div>
          <div style={{ fontSize: '11px', color: '#0066FF', textTransform: 'uppercase', letterSpacing: '1px' }}>{user.role}</div>
        </div>
        <nav style={{ flex: 1, padding: '8px 0' }}>
          {menuItems.map(item => (
            <Link key={item.path} to={item.path} className={`gal-nav-link ${window.location.pathname === item.path ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <span>{item.icon}</span>{item.label}
            </Link>
          ))}
        </nav>
        <div style={{ padding: '12px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button onClick={() => { localStorage.clear(); navigate('/login'); }} style={{ width: '100%', padding: '8px', background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '6px', color: '#DC2626', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>🚪 Logout</button>
        </div>
      </div>

      {/* Main */}
      <div className="gal-main">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ fontFamily: 'Montserrat', fontSize: '28px', fontWeight: 800, color: 'white', marginBottom: '4px' }}>Gallery</h1>
            <p style={{ color: '#475569', fontSize: '14px' }}>Manage photos and videos</p>
          </div>
          <button onClick={() => { setShowUploadModal(true); setSelectedFile(null); setPreview(null); setTitle(''); setDescription(''); }} style={{ background: 'linear-gradient(135deg, #0066FF, #0052CC)', color: 'white', padding: '10px 24px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>
            + Upload
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          {[
            { label: 'Total Items', value: gallery.length, icon: '🖼️', color: '#0066FF' },
            { label: 'Images', value: gallery.filter(g => g.file_type === 'image').length, icon: '📷', color: '#16A34A' },
            { label: 'Videos', value: gallery.filter(g => g.file_type === 'video').length, icon: '🎥', color: '#8B5CF6' },
          ].map((s, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#475569', marginBottom: '6px' }}>{s.label}</div>
                  <div style={{ fontSize: '24px', fontWeight: 800, color: s.color }}>{s.value}</div>
                </div>
                <div style={{ fontSize: '28px' }}>{s.icon}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', color: '#475569', padding: '60px' }}>Loading...</div>
        ) : gallery.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#475569' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🖼️</div>
            <div style={{ fontSize: '16px', marginBottom: '8px' }}>No gallery items yet</div>
            <div style={{ fontSize: '13px' }}>Upload images or videos to get started</div>
          </div>
        ) : (
          <div className="gal-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
            {gallery.map(item => (
              <div key={item.id} className="gal-item">
                {item.file_type === 'image' ? (
                  <img src={item.file_url} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                ) : (
                  <video src={item.file_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                )}
                <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
                  <span style={{ background: item.file_type === 'video' ? 'rgba(139,92,246,0.9)' : 'rgba(0,102,255,0.9)', color: 'white', padding: '2px 8px', borderRadius: '50px', fontSize: '10px', fontWeight: 700 }}>
                    {item.file_type === 'video' ? '🎥 VIDEO' : '📷 IMG'}
                  </span>
                </div>
                <div className="gal-overlay-hover">
                  <button onClick={() => setLightbox(item)} style={{ background: 'white', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', color: '#0f172a' }}>👁 View</button>
                  <button onClick={() => handleDelete(item.id)} style={{ background: 'rgba(220,38,38,0.9)', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', color: 'white' }}>🗑 Delete</button>
                </div>
                {item.title && (
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.8))', padding: '24px 12px 10px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: 'white' }}>{item.title}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
          <div style={{ background: '#0D1B2A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontFamily: 'Montserrat', fontSize: '20px', fontWeight: 800, marginBottom: '24px', color: 'white' }}>Upload to Gallery</h2>

            {/* Type Toggle */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
              {['image', 'video'].map(type => (
                <button key={type} onClick={() => { setUploadType(type); setSelectedFile(null); setPreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} style={{ flex: 1, padding: '10px', border: `2px solid ${uploadType === type ? '#0066FF' : 'rgba(255,255,255,0.1)'}`, borderRadius: '8px', background: uploadType === type ? 'rgba(0,102,255,0.1)' : 'transparent', color: uploadType === type ? '#0066FF' : '#475569', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>
                  {type === 'image' ? '📷 Image' : '🎥 Video'}
                </button>
              ))}
            </div>

            {/* Upload Zone */}
            <div className="upload-zone" onClick={() => fileInputRef.current?.click()} style={{ marginBottom: '16px' }}>
              {preview ? (
                uploadType === 'image' ? (
                  <img src={preview} alt="preview" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', objectFit: 'contain' }} />
                ) : (
                  <video src={preview} controls style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }} />
                )
              ) : (
                <>
                  <div style={{ fontSize: '40px', marginBottom: '12px' }}>{uploadType === 'image' ? '📷' : '🎥'}</div>
                  <div style={{ color: 'white', fontWeight: 600, marginBottom: '4px' }}>Click to select {uploadType}</div>
                  <div style={{ color: '#475569', fontSize: '12px' }}>{uploadType === 'image' ? 'JPG, PNG, WEBP — Max 5MB' : 'MP4, WEBM — Max 50MB'}</div>
                </>
              )}
              <input ref={fileInputRef} type="file" accept={uploadType === 'image' ? 'image/*' : 'video/*'} onChange={handleFileChange} style={{ display: 'none' }} />
            </div>

            {selectedFile && (
              <div style={{ background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.3)', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#16A34A' }}>
                ✅ {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#94A3B8' }}>Title (Optional)</label>
                <input type="text" placeholder="Enter title" value={title} onChange={e => setTitle(e.target.value)} style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#94A3B8' }}>Description (Optional)</label>
                <textarea placeholder="Enter description" value={description} onChange={e => setDescription(e.target.value)} rows={3} style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white', fontSize: '14px', outline: 'none', boxSizing: 'border-box', resize: 'none' }} />
              </div>
            </div>

            {uploading && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#475569', marginBottom: '6px' }}>
                  <span>Uploading...</span><span>{uploadProgress}%</span>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '50px', height: '6px' }}>
                  <div style={{ background: '#0066FF', height: '100%', borderRadius: '50px', width: `${uploadProgress}%`, transition: 'width 0.3s' }} />
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={handleUpload} disabled={!selectedFile || uploading} style={{ flex: 1, padding: '12px', background: selectedFile && !uploading ? 'linear-gradient(135deg, #0066FF, #0052CC)' : 'rgba(255,255,255,0.1)', color: selectedFile && !uploading ? 'white' : '#475569', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 700, cursor: selectedFile && !uploading ? 'pointer' : 'not-allowed' }}>
                {uploading ? `Uploading ${uploadProgress}%...` : '⬆️ Upload'}
              </button>
              <button onClick={() => setShowUploadModal(false)} disabled={uploading} style={{ flex: 1, padding: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '16px', cursor: 'pointer' }}>
          <button onClick={() => setLightbox(null)} style={{ position: 'absolute', top: '16px', right: '16px', background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', color: 'white', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh' }}>
            {lightbox.file_type === 'image' ? (
              <img src={lightbox.file_url} alt={lightbox.title} style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '8px' }} />
            ) : (
              <video src={lightbox.file_url} controls autoPlay style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: '8px' }} />
            )}
            {lightbox.title && <div style={{ textAlign: 'center', marginTop: '12px', color: 'white', fontWeight: 600, fontSize: '16px' }}>{lightbox.title}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default GalleryManagement;
